'use client';

import { useMutation } from '@tanstack/react-query';
import { directApiClient } from '@/lib/api/direct-client';

/**
 * Retry a failed consolidation. POSTs the same endpoint that originally
 * started consolidation — the BE clears failure markers and restarts the
 * workflow (LF-3367 Phase 4). Cached per-file analyses are reused, so the
 * retry is typically 15–25s.
 */
export function useRetryConsolidation(matterId: number | string) {
  const numericId =
    typeof matterId === 'string' ? parseInt(matterId, 10) : matterId;
  return useMutation({
    mutationFn: async () => {
      return directApiClient.post(
        `/matters/${numericId}/ai-analysis/consolidate`,
        {}
      );
    }
  });
}

/**
 * Request a fresh consolidation for the matter. Used after a typed upload
 * (or Additional Documents uploaded via "Upload & extract") that has set
 * `analysisRequested = true` on the presigned-URL payload. Concurrent
 * requests are tolerated by the BE: a second call while
 * `consolidationRequested` is true is a no-op, so the FE never debounces.
 * Failures are surfaced as warning toasts by the caller — the file is on
 * the matter regardless.
 */
export function useRequestConsolidation(matterId: number | string) {
  const numericId =
    typeof matterId === 'string' ? parseInt(matterId, 10) : matterId;
  return useMutation({
    mutationFn: async () => {
      return directApiClient.post(
        `/matters/${numericId}/ai-analysis/consolidate`,
        {}
      );
    }
  });
}
